/**
 * SHX Featured collection — arrows + overflow detection for each product
 * carousel, and tab switching when the section holds several collections
 * (same proven scroller pattern as the collection navigator).
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

class ShxFcol extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;
    const dirFlip = document.body.dataset.rtl === 'true' ? -1 : 1;

    // One controller per zone (tab panel): arrows + overflow detection.
    const zones = Array.from(this.querySelectorAll('.shx-fcol__zone'));
    const updates = new Map();
    this.resizeObserver = new ResizeObserver(() =>
      requestAnimationFrame(() => updates.forEach((update) => update()))
    );

    zones.forEach((zone) => {
      const scroller = zone.querySelector('[data-shx-scroller]');
      if (!scroller) return;
      const prev = zone.querySelector('[data-shx-scroll="-1"]');
      const next = zone.querySelector('[data-shx-scroll="1"]');

      /* SHX §3.20: an arrow that hides itself while holding keyboard focus
         dumps focus onto <body>, losing the visitor's place in the carousel.
         At the ends the arrow now DIMS in place (disabled) and focus is handed
         to the opposite arrow; `hidden` is kept only for "nothing to scroll",
         which is settled on load rather than mid-interaction. Same fix as the
         mega-menu arrows in shx-header.js. */
      /* SHX §3.24: disabling a focused button blurs it synchronously, so the
         caller must capture focus BEFORE disabling (see shx-header.js). */
      const keepFocusInside = (button, hadFocus) => {
        if (!button || !hadFocus) return;
        const other = button === next ? prev : next;
        const fallback =
          (other && !other.disabled && !other.hidden && other) ||
          scroller.querySelector('a, button:not([disabled])');
        fallback?.focus?.({ preventScroll: true });
      };

      const update = () => {
        if (zone.hidden) return;
        const max = scroller.scrollWidth - scroller.clientWidth;
        const pos = Math.abs(scroller.scrollLeft);
        const scrollable = max > 4;
        if (prev) {
          const prevHadFocus = document.activeElement === prev;
          prev.hidden = !scrollable;
          prev.disabled = !scrollable || pos <= 4;
          if (prev.disabled) keepFocusInside(prev, prevHadFocus);
        }
        if (next) {
          const nextHadFocus = document.activeElement === next;
          next.hidden = !scrollable;
          next.disabled = !scrollable || pos >= max - 4;
          if (next.disabled) keepFocusInside(next, nextHadFocus);
        }
      };

      const step = (dir) =>
        scroller.scrollBy({
          left: dir * dirFlip * scroller.clientWidth * 0.75,
          behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth',
        });

      prev?.addEventListener('click', () => step(-1), { signal });
      next?.addEventListener('click', () => step(1), { signal });
      scroller.addEventListener('scroll', update, { passive: true, signal });
      this.resizeObserver.observe(scroller);
      updates.set(zone, update);
      requestAnimationFrame(update);
      setTimeout(update, 300);
    });

    // Tabs — several collections in one section.
    const tabs = Array.from(this.querySelectorAll('[data-shx-tab]'));
    if (tabs.length === 0) return;
    const viewAll = this.querySelector('[data-shx-viewall]');

    const activate = (tab, focus) => {
      tabs.forEach((t) => {
        const on = t === tab;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
      });
      let active = null;
      zones.forEach((zone) => {
        zone.hidden = zone.dataset.shxPanel !== tab.dataset.shxTab;
        if (!zone.hidden) active = zone;
      });
      if (viewAll && active?.dataset.shxUrl) viewAll.href = active.dataset.shxUrl;
      if (active) {
        const update = updates.get(active);
        if (update) requestAnimationFrame(update);
      }
      if (focus) tab.focus();
    };

    tabs.forEach((tab, i) => {
      tab.tabIndex = tab.classList.contains('is-active') ? 0 : -1;
      tab.addEventListener('click', () => activate(tab, false), { signal });
      // Roving arrow-key navigation between tabs (standard tablist behavior).
      tab.addEventListener(
        'keydown',
        (e) => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          e.preventDefault();
          const delta = e.key === 'ArrowRight' ? 1 : -1;
          activate(tabs[(i + delta * dirFlip + tabs.length) % tabs.length], true);
        },
        { signal }
      );
    });

    // Theme editor: clicking a collection block in the sidebar shows its tab.
    if (window.Shopify && Shopify.designMode) {
      this.addEventListener(
        'shopify:block:select',
        (e) => {
          const tab = e.target.closest('[data-shx-tab]');
          if (tab) activate(tab, false);
        },
        { signal }
      );
    }
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.resizeObserver?.disconnect();
  }
}

if (!customElements.get('shx-fcol')) customElements.define('shx-fcol', ShxFcol);
